import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { observer } from "mobx-react-lite";
import { LayoutDashboard, Droplets, Construction, type LucideIcon } from "lucide-react";
import { Spinner } from "@/components/Spinner";
import { useGeneralMapStore } from "./stores/GeneralMapStoreContext";
import type { Perspective } from "./stores/GeneralMapStore";
import { TimePeriodDropdown } from "@/components/TimePeriod/TimeRangeDropdown";
import { I_AND_I_TIME_PERIODS } from "@/config/date";

const PERSPECTIVE_TABS: {
	value: Perspective;
	label: string;
	icon: LucideIcon;
	accent: string;
	tooltip?: string;
}[] = [
	{ value: "overview", label: "Overview", icon: LayoutDashboard, accent: "bg-indigo-500" },
	{ value: "IAndI", label: "I & I", icon: Droplets, accent: "bg-emerald-500", tooltip: "Infiltration & Inflow" },
	{ value: "blockages", label: "Blockages", icon: Construction, accent: "bg-red-500", tooltip: "Blockage Status" },
];

const TAB_TRIGGER_CLASS =
	"relative rounded-md px-1.5 md:px-3 py-1.5 text-xs font-medium flex items-center gap-1 md:gap-1.5 text-gray-400 transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=active]:font-semibold";

export const ViewPerspective = observer(() => {
	const generalMapStore = useGeneralMapStore();

	return (
		<div className="absolute left-14 top-2 md:top-4 z-10 flex items-center gap-1.5 md:gap-2">
			<Tabs
				value={generalMapStore.perspective}
				onValueChange={value => generalMapStore.onPerspectiveChange(value as Perspective)}
			>
				<TabsList className="bg-white/95 backdrop-blur-sm rounded-lg shadow-md p-0.5 flex gap-0">
					{PERSPECTIVE_TABS.map(tab => {
						const Icon = tab.icon;
						const trigger = (
							<TabsTrigger key={tab.value} value={tab.value} className={TAB_TRIGGER_CLASS}>
								<Icon className="h-3.5 w-3.5" />
								<span className="hidden md:inline">{tab.label}</span>
								<span
									className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] w-4/5 rounded-full ${tab.accent} opacity-0 transition-opacity data-[state=active]:opacity-100`}
									data-state={generalMapStore.perspective === tab.value ? "active" : "inactive"}
								/>
							</TabsTrigger>
						);

						if (!tab.tooltip) return trigger;

						return (
							<TooltipProvider key={tab.value} delayDuration={300}>
								<Tooltip>
									<TooltipTrigger asChild>
										<span>{trigger}</span>
									</TooltipTrigger>
									<TooltipContent side="bottom">{tab.tooltip}</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						);
					})}
				</TabsList>
			</Tabs>

			{generalMapStore.perspective === "IAndI" && (
				<>
					<TimePeriodDropdown
						value={generalMapStore.timePeriod}
						onChange={value => generalMapStore.setTimePeriod(value)}
						options={I_AND_I_TIME_PERIODS}
						showLabel={false}
					/>
					{generalMapStore.isIAndILoading && (
						<Spinner size={24} messages={[]} loaderClassName="text-muted-foreground" />
					)}
				</>
			)}

			{generalMapStore.perspective === "blockages" && generalMapStore.isBlockagesLoading && (
				<Spinner size={24} messages={[]} loaderClassName="text-muted-foreground" />
			)}
		</div>
	);
});
